<template>
  <main class="bg-white">
    <v-toolbar flat class="main-header main-header__shadow mb-6 px-5 py-2">
      <v-toolbar-title class="main-header__title">
        <h1>
          <v-icon class="mx-3 mb-1" @click="back">mdi-arrow-left</v-icon>
          <span v-if="!isEdited">{{ $t("new_meeting") }}</span>
          <span v-else>{{ $t("edit_meeting") }}</span>
        </h1>
      </v-toolbar-title>
    </v-toolbar>
    <v-container>
      <v-alert v-if="globalServerErrors !== null" dense outlined type="error">
        <p
          class="mb-0"
          v-for="globalErr in globalServerErrors"
          :key="globalErr"
        >
          {{ globalErr[0] }}
        </p>
      </v-alert>
      <formWrapper :validator="$v.form">
        <form @submit.prevent="handleSave" class="form-box">
          <v-row>
            <v-col cols="12" v-if="isEdited">
              <locale-select @change="fireLocaleChange" />
            </v-col>

            <v-col class="py-0" cols="12" lg="6">
              <form-group name="title" :attribute="`fields.title`">
                <template slot-scope="{ attrs, listeners }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    v-model.trim="form.title"
                    type="text"
                    dense
                  >
                  </v-text-field>
                </template>
              </form-group>
            </v-col>
            <v-col class="py-0" cols="12" lg="6">
              <form-group
                name="duration_in_minutes"
                :attribute="`fields.duration_in_minutes`"
              >
                <template slot-scope="{ attrs, listeners }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    v-model.trim="form.duration_in_minutes"
                    type="number"
                    dense
                  >
                  </v-text-field>
                </template>
              </form-group>
            </v-col>
            <v-col class="py-0" cols="12" lg="6">
              <form-group name="zoom_url" :attribute="`fields.zoom_url`">
                <template slot-scope="{ attrs, listeners }">
                  <v-text-field
                    v-bind="attrs"
                    v-on="listeners"
                    outlined
                    v-model.trim="form.zoom_url"
                    type="text"
                    dense
                  >
                  </v-text-field>
                </template>
              </form-group>
            </v-col>
            <v-col class="py-0" cols="12" lg="6">
              <form-group name="starts_at" :attribute="`fields.starts_at`">
                <template slot-scope="{ attrs, listeners }">
                  <date-time-picker
                    @blur="$v.form.starts_at.$touch()"
                    v-model="form.starts_at"
                    :date-time="form.starts_at"
                    :listeners="listeners"
                    :textFieldProps="{
                      regular: true,
                      label: $t('fields.starts_at'),
                      dense: true,
                      ...attrs,
                    }"
                    timeFormat="HH:mm:ss"
                  ></date-time-picker>
                </template>
              </form-group>
            </v-col>
            <v-col class="py-0" cols="12">
              <form-group name="description" :attribute="`fields.description`">
                <template slot-scope="{ attrs, listeners }">
                  <v-textarea
                    v-bind="attrs"
                    v-on="listeners"
                    no-resize
                    rows="4"
                    outlined
                    v-model.trim="form.description"
                    :label="$t('fields.description')"
                    type="text"
                  >
                  </v-textarea>
                </template>
              </form-group>
            </v-col>

            <v-col cols="12">
              <v-btn
                class="success text-capitalize"
                large
                type="submit"
                width="30%"
                :disabled="$v.form.$invalid"
                :loading="loading"
                ><span>{{ $t("button.save") }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </form>
      </formWrapper>
    </v-container>
  </main>
</template>

<script>
import {
  required,
  minLength,
  maxLength,
  url,
  maxValue,
  minValue,
} from "vuelidate/lib/validators";
import cookie from "js-cookie";

import { StoreData, ShowData, UpdateData } from "@/helpers/apiMethods";

export default {
  name: "ChaptersCrud",

  components: {
    DateTimePicker: () => import("@/components/DateTimePicker"),
    FileUpload: () => import("@/components/fileUpload"),
  },

  data() {
    return {
      globalServerErrors: null,
      isEdited: false,
      loadingData: false,
      loading: false,
      locale: cookie.get("language") || "en",
      reset: false,

      form: {
        title: "",
        zoom_url: "",
        duration_in_minutes: "",
        description: "",
        starts_at: "",
      },
    };
  },

  created() {
    this.editCreateBoolean();
  },
  methods: {
    editCreateBoolean() {
      if (this.$route.name === "meetingsEdit") {
        this.isEdited = true;
      } else {
        this.isEdited = false;
      }
    },

    show(id, locale) {
      ShowData({
        reqName: `admin-panel/chapters/${this.$route.params.id}/meetings`,
        id: id,
        locale,
      })
        .then((res) => {
          const { meeting } = res.data;
          this.form = { ...meeting, starts_at: new Date(meeting.starts_at) };
        })
        .catch(() => {});
    },
    update(formData) {
      this.loading = true;

      UpdateData({
        reqName: `admin-panel/chapters/${this.$route.params.id}/meetings`,
        data: formData,
        id: this.$route.params.slug,
      })
        .then(() => {
          this.isEdited = false;
          this.loading = false;
          this.back();
        })
        .catch((err) => {
          this.globalServerErrors = err.response.data.errors;
          this.scrollTop();
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    buildData() {
      if (this.isEdited) {
        delete this.form.image;
      }
      const dateFormat = "YYYY-MM-DD HH:mm:ss";
      if (this.form.starts_at) {
        this.form.starts_at = this.$moment(this.form.starts_at).format(
          dateFormat
        );
      }
      const formData = new FormData();
      for (const key in this.form) {
        if (this.form[key] !== "") {
          formData.append(key, this.form[key]);
        }
      }

      formData.append("locale", this.locale);

      if (this.isEdited) {
        formData.append("_method", "put");
      }
      return formData;
    },
    handleSave() {
      if (!this.isEdited) {
        this.create(this.buildData());
      } else {
        this.update(this.buildData());
      }
    },

    create(formData) {
      this.loading = true;

      StoreData({
        reqName: `admin-panel/chapters/${this.$route.params.id}/meetings`,
        data: formData,
      })
        .then(() => {
          this.loading = false;

          this.back();
        })
        .catch((err) => {
          this.globalServerErrors = err.response.data.errors;
          this.scrollTop();
          this.loading = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    back() {
      history.go(-1);
    },
    scrollTop() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    fireLocaleChange(locale) {
      this.show(this.$route.params.slug, locale);
      this.locale = locale;
    },
  },
  watch: {
    $route: {
      handler({ params, name }) {
        if (name === "meetingsEdit") {
          this.show(params.slug);
        }
      },
      immediate: true,
    },
  },
  validations: {
    form: {
      title: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(190),
      },

      duration_in_minutes: {
        required,
        minValue: minValue(1),
        maxValue: maxValue(5000),
      },
      zoom_url: {
        required,
        url,
      },
      starts_at: {
        required,
      },

      description: {
        minLength: minLength(1),
        maxLength: maxLength(10000),
      },
    },
  },
};
</script>
